import React, { memo, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Funnel, ChevronsUpDown, Check, Building2, FolderOpen } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import styles from "./HeaderBar.module.css";

export interface ClientMatterFilterMatter {
  matterId: string;
  matterNumber: string;
  matterDescription: string;
  client: string;
}

export interface ClientMatterFilterProps {
  /** Available clients to choose from */
  clients?: string[];
  /** Available matters to choose from */
  matters?: ClientMatterFilterMatter[];
  /** Controlled selected client (null = no filter). Omit for uncontrolled mode. */
  clientFilter?: string | null;
  /** Controlled selected matter id (null = no filter). Omit for uncontrolled mode. */
  matterFilter?: string | null;
  /** Called when client selection changes */
  onClientChange?: (client: string | null) => void;
  /** Called when matter selection changes; second arg carries the auto-filled client (if any) */
  onMatterChange?: (matterId: string | null, client: string | null) => void;
}

export const ClientMatterFilter: React.FC<ClientMatterFilterProps> = memo(({
  clients = [],
  matters = [],
  clientFilter: clientFilterProp,
  matterFilter: matterFilterProp,
  onClientChange,
  onMatterChange,
}) => {
  // Uncontrolled-mode fallback state (used when parent does not pass controlled props)
  const [localClient, setLocalClient] = useState<string | null>(null);
  const [localMatter, setLocalMatter] = useState<string | null>(null);

  const clientFilter = clientFilterProp !== undefined ? clientFilterProp : localClient;
  const matterFilter = matterFilterProp !== undefined ? matterFilterProp : localMatter;

  const setClientFilter = (next: string | null) => {
    if (clientFilterProp === undefined) setLocalClient(next);
    onClientChange?.(next);
  };
  const setMatterFilter = (matterId: string | null, client: string | null) => {
    if (matterFilterProp === undefined) {
      setLocalMatter(matterId);
      if (client !== null) setLocalClient(client);
    }
    onMatterChange?.(matterId, client);
  };

  const [filterOpen, setFilterOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [matterOpen, setMatterOpen] = useState(false);
  const [matterSearch, setMatterSearch] = useState("");

  const clientOptions = clientSearch
    ? clients.filter((c) => c.toLowerCase().includes(clientSearch.toLowerCase()))
    : clients;

  const matterOptions = matterSearch
    ? matters.filter(
        (m) =>
          m.matterNumber.toLowerCase().includes(matterSearch.toLowerCase()) ||
          m.matterDescription.toLowerCase().includes(matterSearch.toLowerCase()),
      )
    : matters;

  const selectedMatter = matterFilter
    ? matters.find((m) => m.matterId === matterFilter) ?? null
    : null;

  const isFilterActive = clientFilter !== null || matterFilter !== null;

  return (
    <Popover
      open={filterOpen}
      onOpenChange={(open) => {
        setFilterOpen(open);
        // Reset inner dropdown state whenever the outer popover closes so that
        // a previously-open Clients/Matters dropdown doesn't reappear on next open.
        // (Radix doesn't always propagate outside-click events to inner popovers
        // before the outer one unmounts its trigger, leading to stuck inner state.)
        if (!open) {
          setClientOpen(false);
          setMatterOpen(false);
          setClientSearch("");
          setMatterSearch("");
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary relative`}
            >
              <Funnel className="h-4 w-4" />
              {isFilterActive && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-card" />
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Filter by Client/Matter</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-[280px] p-3" align="start">
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-foreground">Filter by Client/Matter</h4>

          {/* Client dropdown */}
          <Popover open={clientOpen} onOpenChange={setClientOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              >
                <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate flex-1 text-left">{clientFilter || "All Clients"}</span>
                <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Search clients..."
                  value={clientSearch}
                  onValueChange={setClientSearch}
                />
                <CommandList>
                  <CommandEmpty>No clients found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setClientFilter(null);
                        setMatterFilter(null, null);
                        setClientOpen(false);
                        setClientSearch("");
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", !clientFilter ? "opacity-100" : "opacity-0")} />
                      All Clients
                    </CommandItem>
                    {clientOptions.map((c) => (
                      <CommandItem
                        key={c}
                        value={c}
                        onSelect={() => {
                          setClientFilter(c);
                          setMatterFilter(null, null);
                          setClientOpen(false);
                          setClientSearch("");
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", clientFilter === c ? "opacity-100" : "opacity-0")} />
                        {c}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Matter dropdown */}
          <Popover open={matterOpen} onOpenChange={setMatterOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              >
                <FolderOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate flex-1 text-left">
                  {selectedMatter ? selectedMatter.matterNumber : "All Matters"}
                </span>
                <ChevronsUpDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
              <Command>
                <CommandInput
                  placeholder="Search matters..."
                  value={matterSearch}
                  onValueChange={setMatterSearch}
                />
                <CommandList>
                  <CommandEmpty>No matters found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        setMatterFilter(null, null);
                        setMatterOpen(false);
                        setMatterSearch("");
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", !matterFilter ? "opacity-100" : "opacity-0")} />
                      All Matters
                    </CommandItem>
                    {matterOptions.map((m) => (
                      <CommandItem
                        key={m.matterId}
                        value={`${m.matterNumber} ${m.matterDescription}`}
                        onSelect={() => {
                          setMatterFilter(m.matterId, m.client);
                          setMatterOpen(false);
                          setMatterSearch("");
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 shrink-0",
                            matterFilter === m.matterId ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate text-sm">{m.matterNumber}</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {m.matterDescription} · {m.client}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </PopoverContent>
    </Popover>
  );
});

ClientMatterFilter.displayName = "ClientMatterFilter";

export default ClientMatterFilter;
